import type { NotYet, SymptomPattern } from "./cyclePatterns";
import { dayLabel } from "./date";
import type { SymptomRowId } from "./symptoms";
import type { CyclePhaseDef } from "./types";

/**
 * What Kaya says about each phase of the cycle (ADR-057).
 *
 * The reference app's phase text promised "the best window to push" and "treadmill personal
 * bests" at ovulation. The evidence does not hold that up: across 78 studies performance is at
 * most trivially lower on period days and no different in any other phase (McNulty 2020), an
 * umbrella review calls phase-based training advice premature (Colenso-Semple 2023), and Kaya
 * places a phase by counting days, which cannot find ovulation. So the words live here, not in
 * the generated `data/phases.ts`: the extractor keeps the reference's ids, names and colours and
 * takes the words from this module. Each phase says what it means for the body in one line,
 * never a training instruction; the symptom and weigh-in notes stay, because they are true.
 */

export type PhaseWords = Pick<CyclePhaseDef, "what" | "good" | "easy"> & {
  /** the row's name on Progress › Cycle: plain words, not the textbook one */
  title: string;
  /** the row's one line */
  line: string;
};

export const PHASE_WORDS: Readonly<Record<CyclePhaseDef["id"], PhaseWords>> = {
  menstrual: {
    title: "Period",
    line: "Moving usually eases cramps; the gentle session is there if you want it, and keeping your own session is fine.",
    what: "From day 1 to the end of bleeding: cramps, back ache and tiredness are common, most on the first day or two. Movement is safe and usually eases cramps.",
    good: [
      "The gentle period session, a walk, or your own session if you feel up to it",
      "Heat on the belly or lower back",
      "Belly breathing, stretching, early nights",
    ],
    easy: [
      "Long planks or hard intervals while cramping, if they make it worse",
      // NICE NG88: flooding or changing protection very often is worth a GP visit
      "A heavy day, or soaking through in under 2 hours: the gentle session is a fair choice; if it keeps happening, it is worth seeing a GP",
      "Comparing weight — water retention distorts it",
    ],
  },
  follicular: {
    title: "After the period",
    line: "Nothing to change. Weigh-ins are steadiest here.",
    what: "From the end of the period to ovulation. Some people feel their energy and mood lift; what the body can do in a session does not change in any way that matters.",
    good: ["Your usual programme", "Weigh-ins are most reliable here"],
    easy: ["Nothing in particular"],
  },
  ovulation: {
    title: "Around ovulation",
    line: "Nothing to change.",
    what: "Around the middle of the cycle, roughly 14 days before the next period — an estimate from counting days. Some feel a one-sided twinge or bloating for a day.",
    good: ["Your usual programme"],
    easy: ["Warming up fully and landing softly, as on any day", "A move that sharpens a twinge; it passes in a day or two"],
  },
  luteal: {
    title: "The week before",
    line: "Water weight and appetite rise. If you feel flat, a shorter session still counts.",
    what: "From ovulation to the next period. The last 4–6 days can bring water weight, a bigger appetite, lower energy, poorer sleep and a slightly warmer body.",
    good: [
      "Your usual programme; on a flat day a shorter session still counts",
      "Cycle care in the evening, and a walk",
      // Giersch 2020: core temperature starts higher after ovulation; the hot day's +500 ml covers it
      "On a hot day, take water to the session — the body runs a little warmer after ovulation",
      "Protein and water; an earlier bedtime",
    ],
    easy: ["Weighing yourself in the last few days; 1–2 kg of water is normal", "Over-restricting when cravings hit"],
  },
};

/** Under the phase panel: the rule the whole chapter keeps. */
export const PHASE_NOTE =
  "Strength and fitness do not change in any way that matters by phase; how you feel does. Kaya changes a session for what you log, not for the day of your cycle.";

/** Cycle order, day 1 first — the ring's arcs and the panel's "Next" both walk it. */
export const PHASE_ORDER: readonly CyclePhaseDef["id"][] = ["menstrual", "follicular", "ovulation", "luteal"];

/** Her own pre-period swing is worth naming only once it is more than scale noise. */
const ownSwing = (waterKg?: number | null): string | null =>
  typeof waterKg === "number" && waterKg >= 0.3 ? `+${waterKg.toFixed(1)} kg` : null;

/** A phase's one line; the week before names her own water swing where the scale has shown one. */
export function phaseLine(id: CyclePhaseDef["id"], waterKg?: number | null): string {
  const line = PHASE_WORDS[id].line;
  const kg = ownSwing(waterKg);
  return id === "luteal" && kg ? line.replace("Water weight", `Water weight (${kg} for you)`) : line;
}

/** "Take care with", with her own figure in place of the general one where she has it. */
export function phaseEasy(id: CyclePhaseDef["id"], waterKg?: number | null): string[] {
  const easy = PHASE_WORDS[id].easy;
  const kg = ownSwing(waterKg);
  if (id !== "luteal" || !kg) return [...easy];
  return easy.map((e) => e.replace("1–2 kg of water is normal", `for you that is about ${kg} of water`));
}

/** The first sentence of a phase's `what` — the Next row's second line. */
export function phaseGist(id: CyclePhaseDef["id"]): string {
  const what = PHASE_WORDS[id].what;
  const m = what.match(/^.*?\.(?=\s|$)/);
  return m ? m[0] : what;
}

/** The phase panel's fixed words (ADR-059). "Take care with" is the owner's "precautions". */
export const PANEL_WORDS = {
  thisPhase: "This phase",
  good: "Good to do",
  easy: "Take care with",
  next: "Next",
  back: "Back to today",
  chips: "Show a phase",
} as const;

/** On a period day Food is where iron lives (ADR-039); the panel links there and names no figure. */
export const IRON_ROW = { title: "Iron", line: "A heavy period uses up iron; Progress › Food shows how much you eat" } as const;

/**
 * Why the ring shows no phases (research cycle-and-training.md §4.5). Each says what is true of
 * the gate's reason: `cycleRegularity` reads every logged gap, so an irregular history does not
 * clear itself after three good cycles, and the lines promise nothing it would not keep.
 */
export const NO_PHASES = {
  hormonal: "On a hormonal method, bleeds follow the method, not a cycle, so there are no phases to show.",
  pcos: "Counting days cannot place ovulation with PCOS, so Kaya shows your bleed days and no phases.",
  irregular: "Counting days cannot place ovulation when cycles vary, so Kaya shows your bleed days and no phases.",
  late: "This cycle has run past its usual length, so phases would be guesses; they can come back once you log the next period.",
} as const;

export type NoPhasesWhy = keyof typeof NO_PHASES;

/** "days 17–29", or "day 1" for a one-day run. */
export function daysWord(from: number, to: number): string {
  return from === to ? `day ${from}` : `days ${from}–${to}`;
}

/** "~Sun 11 Oct, in 6 days est." — the Next row's date for the real next phase. */
export function whenWord(dateLabel: string, inDays: number): string {
  const rel = inDays <= 0 ? "today" : inDays === 1 ? "tomorrow" : `in ${inDays} days`;
  return `~${dateLabel}, ${rel} est.`;
}

/** The ring's centre under the phase: "next · Period" over "~Sun 11 Oct est.". */
export function centreNext(id: CyclePhaseDef["id"], dateLabel: string): [string, string] {
  return [`next · ${PHASE_WORDS[id].title}`, `~${dateLabel} est.`];
}

/** The ring's centre when there are no phases. */
export function centreDay(day: number, len: number): string {
  return `Day ${day} of ~${len}`;
}

const TODAY_IN: Readonly<Record<CyclePhaseDef["id"], string>> = {
  menstrual: "a period",
  follicular: "the days after the period",
  ovulation: "the days around ovulation",
  luteal: "the week before",
};

/**
 * The ring's aria-label: what the dot bar said, from the same ranges the arcs draw — "Cycle day 24
 * of about 29: period days 1 to 5, ovulation estimated day 15, luteal days 17 to 29; today in the
 * week before". Without phases it names the bleed days and why there are none.
 */
export function ringLabel(o: {
  day: number;
  len: number;
  period: [number, number];
  ovulationDay: number | null;
  luteal: [number, number] | null;
  current: CyclePhaseDef["id"] | null;
}): string {
  const head = `Cycle day ${o.day} of about ${o.len}: period days ${o.period[0]} to ${o.period[1]}`;
  if (o.ovulationDay === null || !o.luteal) return `${head}; no phases shown`;
  const now = o.current ? `; today in ${TODAY_IN[o.current]}` : "";
  return `${head}, ovulation estimated day ${o.ovulationDay}, luteal days ${o.luteal[0]} to ${o.luteal[1]}${now}`;
}

/* ---------- the symptom diary and its patterns (ADR-060) ---------- */

/** Every map row but flow, which is the period itself, not a symptom of it. */
export type PatternRow = Exclude<SymptomRowId, "flow">;

/** A row's name as a pattern names it; lower-cased inside a sentence. */
export const SYMPTOM_LABEL: Readonly<Record<PatternRow, string>> = {
  pain: "Cramps",
  energy: "Low energy",
  bloating: "Bloating",
  cravings: "Cravings",
  mood: "Low mood",
  sleep: "Poor sleep",
  headache: "Headache",
  tender: "Breast pain",
  backache: "Back ache",
  nausea: "Nausea",
  skin: "Skin",
  digestion: "Digestion",
  dizzy: "Dizziness",
  swelling: "Swelling",
  aches: "Aches",
  fog: "Brain fog",
  hotflush: "Hot flushes",
};

/**
 * What helps on a symptom's days: offers, never orders, and a GP line wherever the symptom can
 * mean something more. `short` closes the Today notice's sentence. The reference sits beside an
 * entry that rests on one; an entry without is ordinary care, not a claim.
 */
export interface CareWords {
  do: string[];
  food: string | null;
  move: string | null;
  gp: string | null;
  short: string;
}

export const CARE: Readonly<Record<PatternRow, CareWords>> = {
  // NICE CKS, breast pain — cyclical
  tender: {
    do: [
      "A well-fitting, supportive bra, for the session too, and a soft one at night",
      "A warm or a cool compress, whichever eases it",
    ],
    food: "Less caffeine on these days helps some people; the evidence is thin, so try it and see",
    move: "Keep to moves with the chest supported: skip jumping and burpees on these days, or take the gentle session",
    gp: "Pain in one spot, a lump, or pain that does not settle after the period is worth a GP visit",
    short: "a supportive bra and chest-supported moves help",
  },
  // Chen 2016 (ginger); Armour 2019, Cochrane (moving); NICE NG73 (the GP line)
  pain: {
    do: ["Heat on the belly or lower back", "Movement usually eases cramps: a walk or the gentle session"],
    food: "Ginger tea has some evidence for easing cramps; water through the day",
    move: "The gentle period session, or your own if you feel up to it",
    gp: "Severe pain that keeps coming back is worth a GP visit",
    short: "heat and a walk help",
  },
  headache: {
    do: ["Water through the day and regular meals", "An earlier night"],
    food: "Do not skip meals; keep caffeine steady rather than stopping it suddenly",
    move: "A walk in daylight; skip hard intervals while it lasts",
    gp: "A new or unusually severe headache, or one with changed vision, is worth a GP visit",
    short: "water and regular meals help",
  },
  bloating: {
    do: ["A walk after meals", "Less salt on these days; water does not add to it"],
    food: "Potassium-rich food — banana, dal, curd — and fewer fizzy drinks",
    move: "Any movement helps the gut along; the scale will read high for a few days",
    gp: null,
    short: "less salt and a walk after meals help",
  },
  cravings: {
    do: [
      "Plan a protein snack for the afternoon rather than banning sweet things",
      "Keep the sweet thing in the plan: a small portion, logged",
    ],
    food: "Protein and fibre at each meal keep the craving smaller",
    move: "A ten-minute walk when the craving hits often sees it off",
    gp: null,
    short: "a planned protein snack helps",
  },
  energy: {
    do: ["A shorter session still counts", "An earlier bedtime for these days"],
    food: "Iron-rich food in the week before and during the period; Progress › Food shows your iron",
    move: "The Short version or a walk; nothing is lost by easing off for a few days",
    gp: "Tiredness that lasts all month, not only these days, is worth a blood test",
    short: "a shorter session still counts",
  },
  // Giersch 2020: core temperature runs higher after ovulation
  sleep: {
    do: [
      "A cooler room: the body runs a little warmer after ovulation",
      "Cycle care in the evening; the breathing settles the night",
    ],
    food: "Caffeine before noon only on these days, and a lighter dinner",
    move: "Move earlier in the day rather than late",
    gp: null,
    short: "a cooler room and an early night help",
  },
  // Pearce 2020: exercise eases premenstrual symptoms
  mood: {
    do: ["Daylight and a walk, most days", "Tell someone close that these are the days"],
    food: "Regular meals: a dropping blood sugar sharpens a low mood",
    move: "Exercise eases premenstrual symptoms — any session you can face, or a walk",
    gp: "A low mood that stops you living normally most months is worth a GP visit; premenstrual dysphoric disorder (PMDD) is real and treatable",
    short: "daylight and a walk help",
  },
  backache: {
    do: ["Heat on the lower back", "Child's pose and cat–cow from Cycle care"],
    food: null,
    move: "Skip loaded moves on the worst day; a walk is fine",
    gp: "Back pain with a fever, or that does not ease after the period, is worth a GP visit",
    short: "heat and gentle mobility help",
  },
  nausea: {
    do: ["Small meals often, and water in sips", "Ginger, fresh or as tea"],
    food: "Plain food on these days: rice, curd, toast",
    move: "Train before eating, not after; skip moves with the head below the heart",
    gp: null,
    short: "small meals and ginger help",
  },
  skin: {
    do: ["Gentle cleansing, and no new products on these days"],
    food: "No food rule is proven for cycle spots",
    move: "Rinse the face after sweating",
    gp: "Spots that scar or do not settle are worth a GP visit",
    short: "gentle cleansing helps",
  },
  digestion: {
    do: ["Water, and a walk after meals"],
    food: "For constipation, fibre and water; for loose stools, plain food and fluids",
    move: "A walk after meals helps either way",
    gp: "Blood in the stool, or symptoms that last beyond the period, are worth a GP visit",
    short: "water and a walk after meals help",
  },
  dizzy: {
    do: ["Stand up slowly", "Eat before training on these days"],
    food: "Water, and a salted snack if your blood pressure runs low",
    move: "Skip moves that go from the floor to standing fast; sit for the rests",
    gp: "Fainting is worth a GP visit",
    short: "standing up slowly and eating first help",
  },
  // one swollen or painful leg can be a clot: the same day, not routine
  swelling: {
    do: ["Legs up for ten minutes in the evening", "Less salt for these days"],
    food: "Potassium-rich food; water does not add to swelling",
    move: "A walk; movement shifts the fluid",
    gp: "Swelling in one leg, or swelling that is painful, needs a GP the same day",
    short: "less salt and legs up help",
  },
  aches: {
    do: ["A longer warm-up", "Heat on what aches"],
    food: null,
    move: "The gentle session or a walk; nothing hard on the worst day",
    gp: null,
    short: "a longer warm-up helps",
  },
  fog: {
    do: ["Lists, and one thing at a time", "Sleep comes first on these days"],
    food: "Regular meals with protein",
    move: "A walk clears the head more than a hard session does",
    gp: null,
    short: "sleep and a walk help",
  },
  hotflush: {
    do: ["A cooler room and layers you can take off", "Cold water to hand"],
    food: "Caffeine, alcohol and very spicy food set them off for some",
    move: "Train in the cooler part of the day",
    gp: "Hot flushes with periods that are changing are worth a GP visit; perimenopause can be managed",
    short: "a cooler room and layers help",
  },
};

/** The symptom page's headings for `CARE`. */
export const CARE_WORDS = { heading: "What helps", food: "Food", move: "Moving", gp: "When to see a GP" } as const;

export const PATTERN_WORDS = {
  heading: "Your patterns",
  /** the symptom page's group: one symptom has one pattern */
  one: "Pattern",
  diary: "Symptom diary",
  logged: (n: number) => `${n} ${n === 1 ? "day" : "days"} logged`,
  forYou: "Usually for you",
  none: "Patterns show once a symptom repeats on the same days in three cycles.",
  noneWait: "after three cycles of symptoms",
  nothing: "Log a day from Today's Cycle row",
  notice: "See the pattern",
} as const;

const NUM = ["no", "one", "two", "three", "four", "five", "six"];
const num = (n: number) => NUM[n] ?? String(n);
const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** "22–25", or "1" for a one-day window — the second window in a sentence. */
function span(from: number, to: number): string {
  return from === to ? String(from) : `${from}–${to}`;
}

/** "Sun 1 – Wed 4 Nov"; the month once when both days share it. */
export function rangeWord(from: string, to: string): string {
  if (from === to) return dayLabel(from);
  const a = dayLabel(from);
  const b = dayLabel(to);
  const [aw, ad, am] = a.split(" ");
  return am === b.split(" ")[2] && from.slice(0, 4) === to.slice(0, 4) ? `${aw} ${ad} – ${b}` : `${a} – ${b}`;
}

/** "days 22–25 · 3 of 3 cycles" */
export function patternLine(p: Pick<SymptomPattern, "from" | "to" | "cycles" | "of">): string {
  return `${daysWord(p.from, p.to)} · ${p.cycles} of ${p.of} cycles`;
}

/**
 * Where the window falls this cycle: "this cycle Sun 1 – Wed 4 Nov · today is day 24",
 * "this cycle Thu 5 – Sat 7 Nov", "came Sun 11 – Mon 12 Oct", and for a period-day pattern the
 * next period's days, as the estimate they are.
 */
export function expectedLine(p: Pick<SymptomPattern, "expected" | "state" | "next">, day: number | null): string {
  if (!p.expected) return "";
  const r = rangeWord(p.expected.from, p.expected.to);
  if (p.state === "past") return p.next ? `came ${r} · next ~${rangeWord(p.next.from, p.next.to)} est.` : `came ${r}`;
  return p.state === "today" && day !== null ? `this cycle ${r} · today is day ${day}` : `this cycle ${r}`;
}

/** The symptom page's first sentence, and its evidence. */
export function patternSentence(p: Pick<SymptomPattern, "label" | "from" | "to" | "cycles" | "of">): {
  text: string;
  basedOn: string;
} {
  return {
    text: `${p.label} has come on ${daysWord(p.from, p.to)} in ${p.cycles} of your last ${p.of} cycles.`,
    basedOn: `${p.cycles} cycles · ${daysWord(p.from, p.to)} each time`,
  };
}

/** The symptom page's second sentence: the expected line, said whole. */
export function expectedSentence(p: Pick<SymptomPattern, "expected" | "state" | "next">, day: number | null): string {
  if (!p.expected) return "";
  const r = rangeWord(p.expected.from, p.expected.to);
  if (p.state === "past")
    return `This cycle it came ${r}.${p.next ? ` Next ~${rangeWord(p.next.from, p.next.to)} est.` : ""}`;
  return p.state === "today" && day !== null ? `This cycle that is ${r}; today is day ${day}.` : `This cycle that is ${r}.`;
}

/** The second sentence's evidence: the start its dates are counted from. */
export function expectedBasedOn(start: string): string {
  return `the period logged ${dayLabel(start)}`;
}

/** "Headache has come on days 26–27 in two cycles; a pattern is named after a third." */
export function notYetLine(n: Pick<NotYet, "label" | "from" | "to" | "cycles">): {
  text: string;
  wait: string;
  basedOn: string;
} {
  const more = Math.max(1, 3 - n.cycles);
  const cycles = n.cycles === 1 ? "cycle" : "cycles";
  return {
    text: `${n.label} has come on ${daysWord(n.from, n.to)} in ${num(n.cycles)} ${cycles}; a pattern is named after a third.`,
    wait: `after ${num(more)} more ${more === 1 ? "cycle" : "cycles"}`,
    basedOn: `${n.cycles} ${cycles} · ${daysWord(n.from, n.to)}`,
  };
}

/** The symptom page's hero clause: "usually moderate"; energy and sleep say "low" and "poor". */
export function typicalWord(row: PatternRow, typical: number): string {
  if (row === "energy") return "usually low";
  if (row === "sleep") return "usually poor";
  return `usually ${["mild", "mild", "moderate", "strong"][Math.max(0, Math.min(3, Math.round(typical)))]}`;
}

/**
 * Today's notice: "Breast pain usually comes around now for you: days 22–25 in 3 of your last 3
 * cycles. Today is day 24; a supportive bra and chest-supported moves help." Two patterns share
 * one sentence, and the closing offer is the first one's.
 */
export function forecastWords(f: {
  when: "today" | "tomorrow";
  day: number;
  patterns: Pick<SymptomPattern, "row" | "label" | "from" | "to" | "cycles" | "of">[];
  more: number;
}): string {
  const [a, b] = f.patterns;
  const short = CARE[a.row].short;
  const same = !b || (a.cycles === b.cycles && a.of === b.of);
  const tail = f.more > 0 ? " And more in the diary." : "";
  if (f.when === "tomorrow") {
    const who = !b
      ? `${lower(a.label)} has come in ${a.cycles} of your last ${a.of} cycles`
      : same
        ? `${lower(a.label)} and ${lower(b.label)} have come in ${a.cycles} of your last ${a.of} cycles`
        : `${lower(a.label)} (${a.cycles} of ${a.of} cycles) and ${lower(b.label)} (${b.cycles} of ${b.of}) have come`;
    return `Tomorrow is day ${f.day}, when ${who}; ${short}.${tail}`;
  }
  const names = b ? `${a.label} and ${lower(b.label)} usually come` : `${a.label} usually comes`;
  const days = !b
    ? `${daysWord(a.from, a.to)} in ${a.cycles} of your last ${a.of} cycles`
    : same
      ? `${daysWord(a.from, a.to)} and ${span(b.from, b.to)} in ${a.cycles} of your last ${a.of} cycles`
      : `${daysWord(a.from, a.to)} (${a.cycles} of ${a.of} cycles) and ${span(b.from, b.to)} (${b.cycles} of ${b.of})`;
  return `${names} around now for you: ${days}. Today is day ${f.day}; ${short}.${tail}`;
}

/** The diary's fixed words. */
export const DIARY_WORDS = {
  title: "Symptom diary",
  label: "Symptoms",
  unit: "days logged",
  empty: "No symptoms logged yet. Log a day from Today's Cycle row.",
  byDay: "By day",
  bySymptom: "By symptom",
  symptoms: "Symptoms",
  logToday: "Log today",
  another: "Another day",
  which: "Which day?",
  open: "Open",
  date: "Date",
  tooOld: "Kaya keeps six months of symptom days",
  showAll: "Show all",
  daysLogged: (n: number) => `Days logged · ${n}`,
  sheetNote:
    "Flow is asked once the period has started; everything else can be logged on any day, and for a past day from the symptom diary. Only what you log is used; symptoms never appear in badges.",
} as const;

/** The diary hero's line: "over 4 cycles · since Thu 6 Aug". */
export function diaryLine(cycles: number, since: string): string {
  return `over ${cycles} ${cycles === 1 ? "cycle" : "cycles"} · since ${dayLabel(since)}`;
}

/** "31 days logged · 4 cycles" — the Symptom diary row. */
export function diaryRowLine(days: number, cycles: number): string {
  return `${PATTERN_WORDS.logged(days)} · ${cycles} ${cycles === 1 ? "cycle" : "cycles"}`;
}

/** A cycle's group in the diary: "This cycle · from Sun 11 Oct" / "Cycle from Sat 12 Sep · 29 days". */
export function cycleHeading(start: string, len: number, current: boolean): string {
  return current ? `This cycle · from ${dayLabel(start)}` : `Cycle from ${dayLabel(start)} · ${len} days`;
}

/** A logged day's row title: "Mon 2 Nov · day 23", "Sun 11 Oct · period day 1". */
export function dayTitle(ds: string, day: number, period: boolean): string {
  return `${dayLabel(ds)} · ${period ? "period day" : "day"} ${day}`;
}

/** "12 days · 4 of 4 cycles" — a symptom's row in By symptom. */
export function tallyLine(days: number, cycles: number, of: number): string {
  return `${days} ${days === 1 ? "day" : "days"} · ${cycles} of ${of} cycles`;
}

/** The symptom page's hero line: "in 4 of 4 cycles · usually moderate". */
export function symptomHeroLine(cycles: number, of: number, typical: string | null): string {
  return [`in ${cycles} of ${of} cycles`, typical].filter(Boolean).join(" · ");
}

export const STRIP_WORDS = {
  now: " · now",
  legend: "light · medium · strong",
  ovulation: "ovulation est.",
} as const;

/** The strips' aria-label: the pattern when there is one, else the count. */
export function stripsLabel(o: {
  label: string;
  cycles: number;
  days: number;
  pattern: Pick<SymptomPattern, "from" | "to" | "cycles" | "of"> | null;
  notYet: Pick<NotYet, "from" | "to" | "cycles"> | null;
}): string {
  const head = `${o.label} by cycle day over ${o.cycles} ${o.cycles === 1 ? "cycle" : "cycles"}`;
  const days = (from: number, to: number) => (from === to ? `day ${from}` : `days ${from} to ${to}`);
  if (o.pattern) return `${head}: ${days(o.pattern.from, o.pattern.to)} in ${o.pattern.cycles} of ${o.pattern.of} cycles`;
  if (o.notYet) return `${head}: ${days(o.notYet.from, o.notYet.to)} in ${num(o.notYet.cycles)} cycles so far`;
  return `${head}: logged on ${o.days} ${o.days === 1 ? "day" : "days"}`;
}
